import UiLanguageSelect from "../components/UiLanguageSelect";
import { useTranslation } from "react-i18next";
// src/pages/ForgotPassword.tsx
import React, { useState } from "react";
import { apiPost } from "../api/client";
import { Link } from "react-router-dom";
import { Button } from "../components/Button";
import { A } from "../styles/appleClasses";

type ForgotOutcome =
  | { kind: "local"; code: "successFallback" | "successKnownResponse" | "sendFailed" }
  | { kind: "raw"; value: React.ReactNode };

type ForgotError = ForgotOutcome | { kind: "known"; key: "accountInlays.errors.tooManyRequests" };

const KNOWN_SUCCESS_MESSAGE = "If your email exists in our system, a reset link will be sent.";

export default function ForgotPassword() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [msg, setMsg] = useState<ForgotOutcome | null>(null);
  const [error, setError] = useState<ForgotError | null>(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSent(false);
    setError(null);
    setMsg(null);
    setLoading(true);

    try {
      const res = await apiPost("/auth/forgot-password", { email: email.trim() });
      setSent(true);
      setMsg(res?.msg === KNOWN_SUCCESS_MESSAGE
        ? { kind: "local", code: "successKnownResponse" }
        : res?.msg ? { kind: "raw", value: res.msg } : { kind: "local", code: "successFallback" });
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      const status = e?.response?.status;
      if (status === 429 && detail === "Too many requests. Please try again later.") setError({ kind: "known", key: "accountInlays.errors.tooManyRequests" });
      else setError(detail ? { kind: "raw", value: detail } : { kind: "local", code: "sendFailed" });
    } finally {
      setLoading(false);
    }
  };

  const successText = msg?.kind === "raw" ? msg.value
    : msg?.code === "successKnownResponse" ? t("forgotPassword.successKnownResponse")
    : msg ? t("forgotPassword.successFallback") : "";
  const errorText = error?.kind === "raw" ? error.value
    : error?.kind === "known" ? t(error.key) : error ? t("forgotPassword.sendFailed") : "";

  const alertBase = `mt-4 rounded-2xl border px-4 py-3 ${A.body}`;

  return (
    <div className={`min-h-screen bg-gray-50 ${A.body} flex justify-center px-3 py-10`}>
      <div className={`${A.card} w-full max-w-md p-6`}>
        <div className="mb-4 flex justify-end"><UiLanguageSelect /></div>
        <div className="text-center mb-4">
          <h2 className={A.title}>{t("forgotPassword.title")}</h2>
          <div className={`${A.label} mt-1`}>{t("forgotPassword.explanation")}</div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className={`block mb-1 ${A.label}`}>{t("directory.common.email")}</label>
            <input
              type="email"
              className={A.input}
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
            />
          </div>

          <Button
            className="w-full justify-center"
            type="submit"
            variant="primary"
            disabled={loading}
          >
            {loading ? t("login.sendingDots") : t("forgotPassword.sendResetLink")}
          </Button>
        </form>

        {sent && (
          <div className={`${alertBase} border-green-200 bg-green-50 text-green-700`}>
            {successText}
          </div>
        )}

        {errorText && (
          <div className={`${alertBase} border-red-200 bg-red-50 text-red-700`}>
            {errorText}
          </div>
        )}

        <div className={`text-center mt-5 ${A.label}`}>
          <Link to="/login" className="text-appleblue hover:underline">
            {t("register.backToLogin")}
          </Link>
        </div>
      </div>
    </div>
  );
}
